/**
 * What the signed-in member's plan includes.
 *
 * The server resolves this now: `/site-settings` returns a
 * `mem_subscription.capabilities` map, which already accounts for an
 * Enterprise agreement that negotiated a capability in or out. The UI reads
 * that map rather than re-deriving entitlements from the plan name, because a
 * plan name cannot describe "Commercial but not Weather".
 *
 * The local rules below stay as a fallback for the payloads that predate the
 * capabilities map — a browser holding a cached session, or a frontend
 * deployed ahead of the backend. They are the rules that were in this file
 * before, so falling back lands on exactly the old behaviour.
 */

export const CAPABILITIES = {
  COMMERCIAL_PROPERTIES: 'commercial_properties',
  INVESTOR_PORTFOLIO: 'investor_portfolio',
  WEATHER_DATA: 'weather_data',
  CONTACT_INFORMATION: 'contact_information',
  CREDITS: 'credits',
  EXPORTS: 'exports',
};

export const COMMERCIAL_RESTRICTION_DATE = '2026-09-22';

/**
 * The offset an API timestamp without one is read in.
 *
 * Bare `new Date('2026-09-21 21:00:00')` reads the string as BROWSER-local, so
 * the same subscription row was grandfathered in London and restricted in Los
 * Angeles. Reading it as UTC — the same zone the cutoff below resolves to —
 * makes the answer identical everywhere and keeps the boundary exactly where
 * the tests have always pinned it.
 *
 * KNOWN GAP, deliberately not closed here. The backend does
 * `Carbon::parse($createdAt)->utc()` on the same naive string, and
 * `Carbon::parse` uses the app timezone, which is `Asia/Kolkata`
 * (config/app.php) — while its cutoff is parsed explicitly as UTC
 * (PlanEntitlements::restrictionDate). So the backend's boundary sits 5h30
 * earlier than this one, and a signup in that window is grandfathered by the
 * API and restricted by the UI. Closing it moves who keeps paid access, so it
 * needs a backend change and a product call, not a quiet edit here. The parity
 * test pins both sides so the gap stays visible.
 */
const API_TIMESTAMP_UTC_OFFSET_MINUTES = 0;

/** A trailing `Z`, `+HH:MM`, `+HHMM` or `-HH:MM` — not just any colon. */
const EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Epoch milliseconds for a subscription timestamp, or NaN if unreadable.
 *
 * A timestamp that carries its own offset is authoritative and used as-is.
 * One without an offset is read at a fixed offset, never the browser's. The
 * space separator is replaced because 'Y-m-d H:i:s' is not a format the spec
 * requires engines to accept — Safari returns Invalid Date for it, which used
 * to make the comparison false and restrict a grandfathered subscription.
 */
const parseSubscriptionTimestamp = (raw) => {
  if (!raw) {
    return NaN;
  }

  const value = String(raw).trim();
  if (EXPLICIT_OFFSET.test(value)) {
    return Date.parse(value.replace(' ', 'T'));
  }

  const parts = value
    .replace('T', ' ')
    .match(/^(\d{4})-(\d{2})-(\d{2})(?:[ ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!parts) {
    return NaN;
  }

  const [, year, month, day, hour = '0', minute = '0', second = '0'] = parts;

  return (
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ) -
    API_TIMESTAMP_UTC_OFFSET_MINUTES * 60 * 1000
  );
};

/**
 * Built-in rule: new subscribers on these plans lose commercial and the
 * portfolio. Never owner contacts — residential contacts are on every plan.
 */
const RESTRICTED_PLANS = ['Rhino Lite', 'Rhino Pro'];

/** Built-in rule: plan ids that have never included Weather Reports. */
const WEATHER_BLOCKED_PLAN_IDS = [89];

const subscriptionOf = (memData) =>
  memData?.mem_subscription || memData?.user_subscription_exist || null;

/**
 * Whether memData has actually told us which plan the member is on.
 *
 * memData arrives asynchronously. Before it lands we know nothing, and
 * "nothing" must not be rendered as "restricted" — otherwise a Legend customer
 * sees blurred cards and a locked portfolio for the first few hundred
 * milliseconds of every page load.
 */
export const subscriptionIsKnown = (memData) =>
  Boolean(subscriptionOf(memData)?.plan_name);

/**
 * The server's answer for one capability, or null if this payload doesn't
 * carry one. Only a real boolean counts: a missing or malformed entry means
 * "not answered", never "denied".
 */
export const serverCapability = (memData, capability) => {
  const value = subscriptionOf(memData)?.capabilities?.[capability];

  return typeof value === 'boolean' ? value : null;
};

/**
 * Built-in commercial/portfolio rule: Lite and Pro subscriptions that
 * started on or after the restriction date lose them; earlier ones are
 * grandfathered.
 */
const legacyCommercialAccess = (memData) => {
  const subscription = subscriptionOf(memData);
  if (!subscription) {
    return false;
  }

  const planName = subscription.plan_name || '';

  // A subscription row with no plan on it tells us nothing; it must not read
  // as "not one of the restricted plans, therefore entitled".
  if (!planName) {
    return false;
  }

  if (!RESTRICTED_PLANS.includes(planName)) {
    return true;
  }

  // `created_date` is the column this legacy table actually declares as
  // fillable; `created_at` is not always populated. The backend reads both, in
  // this order (PlanEntitlements::isNewRestrictedSubscriber), so reading only
  // `created_at` here restricts rows the API happily serves.
  const subscriptionCreatedAt =
    subscription.created_at || subscription.created_date || null;

  const createdAt = parseSubscriptionTimestamp(subscriptionCreatedAt);

  // No readable start date is not evidence of a new signup. The backend
  // grandfathers this case explicitly — "guessing 'new' would strip a paying
  // customer's access" — and the UI has to agree with the enforcement point,
  // or an entitled member gets a locked screen over data the API returns.
  // Note this is deliberately the one place the fail-closed default does not
  // apply: the plan IS known here, only its start date is not.
  if (Number.isNaN(createdAt)) {
    return true;
  }

  // Cutoff is a bare date, so Date.parse reads it as UTC midnight — the same
  // instant the backend's restrictionDate() resolves to.
  return createdAt < Date.parse(COMMERCIAL_RESTRICTION_DATE);
};

/** Built-in weather rule: the plan-id blocklist the details page used to hold. */
const legacyWeatherAccess = (memData) => {
  // Same subscription every other capability resolves from. This used to read
  // user_subscription_exist first, preserving the precedence of the plan-id
  // check that lived in Property-details.jsx; the server sets both fields to
  // the same object, so the only effect was weather disagreeing with the rest.
  const planId = subscriptionOf(memData)?.plan_id || null;

  return Boolean(planId) && !WEATHER_BLOCKED_PLAN_IDS.includes(planId);
};

const LEGACY_RULES = {
  [CAPABILITIES.COMMERCIAL_PROPERTIES]: legacyCommercialAccess,
  [CAPABILITIES.INVESTOR_PORTFOLIO]: legacyCommercialAccess,
  // Owner contacts are on every public plan; only an agreement (the server's
  // capabilities map) can take them away.
  [CAPABILITIES.CONTACT_INFORMATION]: () => true,
  [CAPABILITIES.WEATHER_DATA]: legacyWeatherAccess,
  // Credits and exports have never been plan-gated in the UI; only an explicit
  // server answer can take them away.
  [CAPABILITIES.CREDITS]: () => true,
  [CAPABILITIES.EXPORTS]: () => true,
};

/**
 * Does this member's plan include the capability?
 *
 * Fail-closed on the capabilities the UI blurs: anything we cannot positively
 * confirm as entitled reads as not entitled. Pair with subscriptionIsKnown()
 * (or use planIsMissingCapability) before gating UI, so a page that has not
 * loaded memData yet does not flash a locked state at an entitled customer.
 */
export const planIncludes = (memData, capability) => {
  if (!memData || !subscriptionOf(memData)) {
    return false;
  }

  const answered = serverCapability(memData, capability);
  if (answered !== null) {
    return answered;
  }

  return (LEGACY_RULES[capability] || (() => true))(memData);
};

/** The plan the public upsell points at. */
export const UPGRADE_PLAN_NAME = "Rhino Legend";

/**
 * The plan we can honestly tell this member to upgrade to, or null.
 *
 * "Upgrade to Rhino Legend" is only true for someone the built-in Lite/Pro
 * rule is what's denying. An agreement can now exclude a capability from any
 * plan, so that copy could otherwise land in front of a Legend customer and
 * tell them to buy what they already have.
 */
export const upgradeTargetFor = (memData) =>
  RESTRICTED_PLANS.includes(subscriptionOf(memData)?.plan_name || "")
    ? UPGRADE_PLAN_NAME
    : null;

/**
 * The upsell line for a locked feature.
 *
 * `suffix` completes "Upgrade to Rhino Legend …" for the members that sentence
 * is true for; everyone else gets `generic`, which names no plan. The overlay
 * renders its `title` above this, so the feature being locked is still named
 * even when the message itself is generic.
 */
export const upgradeMessage = (
  memData,
  suffix = "for access",
  generic = "Not included on your plan."
) => {
  const target = upgradeTargetFor(memData);

  return target ? `Upgrade to ${target} ${suffix}` : generic;
};

/** The gate UI should use: restrict only once the plan is actually known. */
export const planIsMissingCapability = (memData, capability) =>
  subscriptionIsKnown(memData) && !planIncludes(memData, capability);
